/**
 * NotificationsCard — Settings panel for opting into breach alerts.
 *
 * Shows one of three states based on the browser:
 *   • Unsupported  → can't enable, with a hint about iOS PWA install.
 *   • Blocked      → permission denied, with re-enable instructions.
 *   • Default/On   → an enable/disable switch + "Send test" button.
 *
 * The "site_id" prop pins this device to receive only that site's
 * breaches. Pass "" to receive from every site the user can access.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, BellOff, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  pushSupported,
  notificationPermission,
  getActiveSubscription,
  enablePush,
  disablePush,
  sendTestNotification,
  isStandalone,
} from "@/lib/pushNotifications";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export default function NotificationsCard({ siteId = "" }) {
  const supported = pushSupported();
  const [perm, setPerm] = useState(notificationPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const ios = isIOS();
  const standalone = isStandalone();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sub = await getActiveSubscription();
      if (!cancelled) setSubscribed(!!sub);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        await enablePush(siteId);
        setPerm("granted");
        setSubscribed(true);
        toast.success("Breach alerts enabled on this device");
      } else {
        await disablePush();
        setSubscribed(false);
        toast.success("Breach alerts disabled on this device");
      }
    } catch (err) {
      toast.error(err.message || "Could not change notification settings");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const res = await sendTestNotification();
      if (res?.sent) {
        toast.success(`Test sent to ${res.sent} device${res.sent === 1 ? "" : "s"}`);
      } else {
        toast.warning("No device received the test — try re-enabling notifications");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Could not send test";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="surface p-6" data-testid="push-unsupported">
        <div className="flex items-center gap-2 mb-2">
          <BellOff className="w-5 h-5 text-charcoal-soft" />
          <h3 className="font-display font-bold text-lg">Breach alerts</h3>
        </div>
        <p className="text-sm text-charcoal-soft">
          This browser doesn&apos;t support push notifications.
          {ios && !standalone && (
            <> Install HACCP AU to your home screen first — iOS only delivers push to installed PWAs.</>
          )}
        </p>
      </div>
    );
  }

  if (perm === "denied") {
    return (
      <div className="surface p-6" data-testid="push-denied">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-ochre-600" />
          <h3 className="font-display font-bold text-lg">Breach alerts are blocked</h3>
        </div>
        <p className="text-sm text-charcoal-soft">
          You previously denied notifications. Re-enable them in your browser&apos;s site
          settings (the lock icon in the address bar → Notifications → Allow), then refresh
          this page.
        </p>
      </div>
    );
  }

  return (
    <div className="surface p-6 space-y-4" data-testid="push-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-5 h-5 text-eucalyptus-700" />
            <h3 className="font-display font-bold text-lg">Breach alerts</h3>
          </div>
          <p className="text-sm text-charcoal-soft max-w-prose">
            Get a phone notification the moment a fridge, freezer or cool-room reading goes
            out of range — even if nobody has the app open. Works on installed PWAs (iOS 16.4+)
            and any modern desktop browser.
          </p>
          {subscribed && (
            <div className="mt-2 flex items-center gap-1 text-sm text-eucalyptus-700">
              <CheckCircle2 className="w-4 h-4" />
              <span data-testid="push-enabled-pill">Enabled on this device</span>
            </div>
          )}
        </div>
        <Switch
          data-testid="push-toggle"
          checked={subscribed}
          disabled={busy}
          onCheckedChange={handleToggle}
        />
      </div>

      {subscribed && (
        <div className="flex gap-2">
          <Button
            data-testid="push-test-btn"
            onClick={handleTest}
            disabled={busy}
            variant="outline"
            className="h-10 gap-2 border-eucalyptus-700 text-eucalyptus-900"
          >
            <Bell className="w-4 h-4" /> Send test notification
          </Button>
        </div>
      )}
    </div>
  );
}
